import { makeStyles, OverlayDrawer, tokens } from '@fluentui/react-components';
import { useState } from 'react';
import { Outlet, useLocation } from 'react-router';

import { useIsNarrow } from '@/hooks/useMediaQuery';

import { navStorage } from './navStorage';
import { SideNav } from './SideNav';
import { TOP_BAR_HEIGHT, TopBar } from './TopBar';

const useStyles = makeStyles({
  shell: {
    minHeight: '100dvh',
    display: 'grid',
    // Sidebar sizes itself; the main column takes everything else. Full width
    // by design — no max-width on the shell or on <main>.
    gridTemplateColumns: 'auto 1fr',
    gridTemplateRows: `${TOP_BAR_HEIGHT} 1fr`,
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground1,
  },
  main: {
    gridColumn: 2,
    minWidth: 0, // lets wide tables scroll instead of stretching the grid
    padding: tokens.spacingVerticalL,
    boxSizing: 'border-box',
  },
  skipLink: {
    position: 'absolute',
    insetInlineStart: tokens.spacingHorizontalM,
    insetBlockStart: '-4rem',
    zIndex: 1000,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow16,
    textDecorationLine: 'none',
    ':focus-visible': {
      insetBlockStart: tokens.spacingVerticalM,
      outlineWidth: tokens.strokeWidthThick,
      outlineStyle: 'solid',
      outlineColor: tokens.colorStrokeFocus2,
    },
  },
});

export function AppShell() {
  const styles = useStyles();
  const isNarrow = useIsNarrow();
  const location = useLocation();

  // Read in the initialiser, not an effect, so a collapsed sidebar never
  // renders expanded for a frame first.
  const [collapsed, setCollapsed] = useState(() => navStorage.getCollapsed() ?? false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // On a narrow screen the hamburger opens a drawer instead of collapsing.
  const toggleNav = () => {
    if (isNarrow) {
      setDrawerOpen((open) => !open);
      return;
    }

    setCollapsed((current) => {
      navStorage.setCollapsed(!current);
      return !current;
    });
  };

  // Close the drawer after navigating, or it covers the page just reached.
  // Link clicks are handled by onNavigate; this catches back/forward too.
  // Adjusted during render (React's documented pattern) rather than in an
  // effect, which would paint the open drawer for a frame first.
  const [lastPath, setLastPath] = useState(location.pathname);
  if (location.pathname !== lastPath) {
    setLastPath(location.pathname);
    if (drawerOpen) setDrawerOpen(false);
  }

  return (
    <div className={styles.shell}>
      {/* Now skips both the top bar and the sidebar, since <main> follows both. */}
      <a className={styles.skipLink} href="#main-content">
        Skip to main content
      </a>

      <TopBar onToggleNav={toggleNav} navExpanded={isNarrow ? drawerOpen : !collapsed} />

      {isNarrow ? (
        // OverlayDrawer brings the focus trap, Escape handling and focus
        // restoration to the hamburger with it.
        <OverlayDrawer
          open={drawerOpen}
          onOpenChange={(_event, data) => setDrawerOpen(data.open)}
          position="start"
          modalType="modal"
        >
          <SideNav collapsed={false} forceExpanded onNavigate={() => setDrawerOpen(false)} />
        </OverlayDrawer>
      ) : (
        <SideNav collapsed={collapsed} />
      )}

      <main id="main-content" className={styles.main} tabIndex={-1}>
        <Outlet />
      </main>
    </div>
  );
}
